export default defineNuxtPlugin({
  setup() {
    const CSRF_COOKIE = "XSRF-TOKEN"
    const CSRF_HEADER = "X-XSRF-TOKEN"
    
    const getApiBaseUrl = () => {
      const { API_URL } = useRuntimeConfig().public
      
      const url = new URL(API_URL)

      url.hostname = url.hostname

      return url.toString()
    }
    
    async function initCsrf() {
      const existingToken = useCookie(CSRF_COOKIE, {
        maxAge: undefined,
        expires: undefined
      }).value;
      const apiBaseUrl = getApiBaseUrl()

      if (existingToken) return existingToken;

      await $fetch("/sanctum/csrf-cookie", {
        baseURL: apiBaseUrl,
        credentials: "include",
      });

      return useCookie(CSRF_COOKIE, {
        maxAge: undefined,
        expires: undefined
      }).value;
    }

    const api = $fetch.create({
      credentials: "include",
      async onRequest({ request, options }) {
        const rateLimitHit = useState<boolean>('rate-limit-hit')
        const { BASE_URL } = useRuntimeConfig().public
        let token = useCookie(CSRF_COOKIE, {
          maxAge: undefined,
          expires: undefined
        }).value
        const method = options.method?.toLowerCase() ?? 'post';
        const url = typeof request === 'string' ? request : request.toString()

        if (options.body instanceof FormData && method === 'put') {
          options.method = 'post';
          options.body.append('_method', 'PUT');
        }

        if (
          import.meta.client &&
          ['post', 'delete', 'put', 'patch'].includes(
            options?.method?.toString().toLowerCase() ?? ''
          )
        ) {
          token = await initCsrf();
        }

        let headers: HeadersInit = {
          accept: "application/json",
        }

        if (import.meta.server) {
          const cookieString = useRequestHeaders(["cookie"]).cookie
      
          headers = {
            ...headers,
            ...(cookieString && { cookie: cookieString }),
            referer: BASE_URL,
          };
        }
        
        if (token) {
          headers[CSRF_HEADER] = token
        }

        let firebaseToken: string | null = null
        if (process.client) {
          try {
            const { $firebaseAuth } = useNuxtApp()
            const user = $firebaseAuth?.currentUser
            if (user) {
              firebaseToken = await user.getIdToken()
              if (firebaseToken) {
                headers['Authorization'] = `Bearer ${firebaseToken}`
              }
            }
          } catch (e) {
          }
        }

        if (process.client && firebaseToken) {
          const { validateHttps, signRequest, generateRequestId, shouldSkipSigning, getSessionSecret } = useRequestSecurity()
          
          if (!shouldSkipSigning(url, method)) {
          try {
              validateHttps()
          } catch (error: any) {
            if (error.message?.includes('SECURITY_HTTPS_REQUIRED')) {
              throw error
            }
          }

          const requestId = generateRequestId()
          headers['X-Request-ID'] = requestId

          if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
            try {
              const sessionSecret = getSessionSecret()
              const signedBody = await signRequest(options.body as Record<string, any>, sessionSecret || undefined, firebaseToken)
              options.body = signedBody
            } catch (error) {
                console.error('Failed to sign request:', error)
              throw error
              }
            }
          }
        }

        if (rateLimitHit.value) {
          options = {
            ...options,
            timeout: 1
          }
        } 

        const existingHeaders = options.headers || {}
        const existingHeadersObj = existingHeaders instanceof Headers 
          ? Object.fromEntries(existingHeaders.entries())
          : (typeof existingHeaders === 'object' ? existingHeaders : {})
        
        const mergedHeaders = new Headers({
          ...headers,
          ...existingHeadersObj
        })
        options.headers = mergedHeaders
        options.baseURL = getApiBaseUrl()
      },
      onRequestError({ error }) {
        const { endFetch } = usePageLoading()
        endFetch()
      },
      async onResponse({ response, options }) {
        const { endFetch } = usePageLoading()
        endFetch()

        if (process.client && response.headers) {
          const signature = response.headers.get('X-Response-Signature')
          const timestamp = response.headers.get('X-Response-Timestamp')
          const nonce = response.headers.get('X-Response-Nonce')

          if (signature && timestamp && nonce) {
            try {
              const { verifyResponse, getSessionSecret, setSessionSecret } = useRequestSecurity()
              
              if (response._data && typeof response._data === 'object' && response._data.session_secret) {
                setSessionSecret(response._data.session_secret)
              }
              
              const sessionSecret = getSessionSecret()
              if (sessionSecret) {
                const responseContent = typeof response._data === 'string' 
                  ? response._data 
                  : JSON.stringify(response._data)
                
                const isValid = await verifyResponse(responseContent, signature, timestamp, nonce, sessionSecret)
                
                if (!isValid) {
                  console.error('Response signature verification failed')
                  throw new Error('SECURITY_RESPONSE_INVALID: Response signature verification failed')
                }
              }
            } catch (error: any) {
              if (error.message?.includes('SECURITY_RESPONSE_INVALID')) {
                throw error
              }
              console.error('Response verification error:', error)
            }
          }
        }
      },
      async onResponseError({ response }) {
        const { endFetch } = usePageLoading()
        endFetch()
        const route = useRoute();
        const rateLimitHit = useState<boolean>('rate-limit-hit');
        const redirectTo = route.fullPath === '/' ? '/dashboard' : route.fullPath
        
        if (response.status === 401 || response.status === 419) {
          if (process.client) {
            const isNavigating = useState<boolean>('auth-navigating', () => false)
            if (isNavigating.value) return
            
            isNavigating.value = true
            try {
              const { reset } = useAuth()
              const { isFirebaseAuthenticated } = useAuthFirebase()
              const { signOut: firebaseSignOut } = useFirebaseAuth()
              
              if (isFirebaseAuthenticated.value) {
                try {
                  await firebaseSignOut().catch(() => {})
                } catch (error) {
                  console.error('Firebase sign out failed:', error)
                }
              }
              
              reset()
              
              const xsrfCookie = useCookie("XSRF-TOKEN", {
                maxAge: undefined,
                expires: undefined
              })
              xsrfCookie.value = null
            } catch (e) {
              console.error('Error clearing auth state:', e)
            }
            await navigateTo(`/login?redirectTo=${redirectTo}`)
          }
        }

        if (response.status === 429 && !rateLimitHit.value) {
          const responseData = response._data
          if (responseData && responseData.code === 'SECURITY_RATE_LIMIT_EXCEEDED') {
            await navigateTo({
              path: '/unauthorized-access',
              query: {
                code: responseData.code,
                message: responseData.message || 'Rate limit exceeded',
                action_required: responseData.action_required || 'wait_and_retry'
              }
            })
            return
          }
          
          rateLimitHit.value = true
  
          const { start } = useTimeout(60 * 1000, { controls: true, callback: () => rateLimitHit.value = false })
  
          start()
          useErrorToast({ message: 'Please wait a minute then refresh the page.', title: 'Network Error!' })
        }
  
        if (response.status === 403) {
          const responseData = response._data
          
          if (responseData && typeof responseData === 'object' && responseData.code) {
            const unauthorizedCodes = [
              'SECURITY_REFERER_INVALID',
              'SECURITY_USER_AGENT_INVALID',
              'SECURITY_RATE_LIMIT_EXCEEDED',
              'SECURITY_TOKEN_INVALID',
              'SECURITY_IP_NOT_WHITELISTED',
              'SECURITY_SIGNATURE_INVALID',
              'SECURITY_HTTPS_REQUIRED'
            ]
            
            if (unauthorizedCodes.includes(responseData.code)) {
              await navigateTo({
                path: '/unauthorized-access',
                query: {
                  code: responseData.code,
                  message: responseData.message || 'Access denied',
                  action_required: responseData.action_required || 'use_download_button'
                }
              })
              return
            }
          }
          
          const errorMessage = responseData?.message || responseData?.error || 'You do not have permission to access this resource.'
          const isAuthorizationError = typeof errorMessage === 'string' && (
            errorMessage.includes('Unauthorized') ||
            errorMessage.includes('can only') ||
            errorMessage.includes('your own')
          )
          
          if (isAuthorizationError) {
            await navigateTo({
              path: '/forbidden',
              query: {
                message: errorMessage
              }
            })
            return
          }
          
          await navigateTo('/session-expired')
        }
        
        if (![429, 422, 401, 403].includes(response.status)) {
          let errorMessage = 'Something went wrong!'
          
          if (response.status === 500) {
            errorMessage = 'Server error. Please try again in a few minutes.'
          } else if (response.status === 502 || response.status === 503 || response.status === 504) {
            errorMessage = 'Service temporarily unavailable. Please try again later.'
          } else if (response.status === 413) {
            errorMessage = 'File too large. Please choose a smaller file.'
          } else if (response.status === 0) {
            errorMessage = 'Connection error. Please check your internet connection.'
          }          
        }
      }
    })

    return {
      provide: {
        api
      }
    }
  }
})